import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { personLabel } from '../../components/ApprovalLevels.js';

// L4.03 — Escalation register (view).
// GET /v1/wf/escalations → { rows, total }

interface WfEscalation {
  readonly escalationId: string;
  readonly escalationLevel: number;
  readonly escalationTrigger: string;
  readonly escalationAction: string;
  readonly escalatedToUserId: string | null;
  /** Whom it went to, named by the server (G18). */
  readonly actorName: string | null;
  readonly actorCode: string | null;
  readonly escalatedAt: string;
  readonly isResolved: boolean;
}

export function WorkflowEscalationsPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [rows, setRows] = useState<WfEscalation[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<{ rows: WfEscalation[]; total: number }>('/v1/wf/escalations', { token })
      .then((r) => { if (alive) { setRows(r.rows); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  return (
    <main aria-label="wf-escalations">
      <h1 style={{ fontSize: 20, marginBottom: 12 }}>{t('wf.escalations.title')}</h1>
      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!rows ? (
        <p>{t('common.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="escalations-empty" style={{ color: 'var(--color-text-muted)' }}>{t('wf.escalations.empty')}</p>
      ) : (
        <DataTable<WfEscalation>
          ariaLabel="escalations-table"
          rows={rows}
          rowKey={(e) => e.escalationId}
          rowTestId={(e) => `escalation-row-${e.escalationId}`}
          columns={[
            { key: 'level', header: t('wf.escalations.colLevel'), render: (e) => e.escalationLevel },
            { key: 'trigger', header: t('wf.escalations.colTrigger'), render: (e) => e.escalationTrigger },
            { key: 'action', header: t('wf.common.action'), render: (e) => e.escalationAction },
            { key: 'escalatedTo', header: t('wf.escalations.colEscalatedTo'), render: (e) => personLabel(e.actorName, e.actorCode) ?? '—' },
            { key: 'at', header: t('wf.escalations.colAt'), render: (e) => at(e.escalatedAt) },
            { key: 'resolved', header: t('wf.escalations.colResolved'), render: (e) => (e.isResolved ? t('wf.common.yesLower') : t('wf.common.noLower')) },
          ]}
        />
      )}
    </main>
  );
}
